import React, { useCallback, useState } from "react";
import toDataUrl from "../../utils/toDataUrl";
import { PrinterIcon } from "@heroicons/react/24/solid";
import toast from "react-hot-toast";
import moment from "moment";
import Button from "../common/Button";
import { useNavigate } from "react-router-dom";
import request from "../../utils/fetch";
import Spinner from "../common/Spinner";

async function handlePrint(data) {
  try {
    const [logo, { default: createReport }] = await Promise.all([
      toDataUrl("/PC_Horz_Color.png"),
      import ("../../reports/generate/reportSummary"),
    ]);

    createReport({ data, logo });
  } catch (e) {
    toast.error(e.response?.data?.error_message || e.message || "Something went wrong");
  }
}

// NOTE: The content of this is mostly copied directly from the original.
export default function ReportConfirmation({ data, advanceTab, advanceLabel, readOnly }) {
  const navigate = useNavigate();

  const [saveStatus, setSaveStatus] = useState("clean");

  const handleSubmit = useCallback(() => {
    setSaveStatus("saving");

    request({ method: "PUT", url: `/reports/${data._id}/submit`}).then(() => {
        toast.success("Report submitted successfully");
        navigate("/reports");
      }).catch((e) => {
        toast.error(e.response?.data?.message ?? e.message);
      }).finally(() => {
        setSaveStatus("clean");
      });
  }, [data, navigate]);

  const readyToSubmit = saveStatus !== "saving" && !advanceLabel;

  return (<>
    <div className="w-full flex justify-between">
      <h3 className="w-full text-2xl font-semibold flex justify-between">Summary</h3>
      <PrinterIcon
        width={24}
        height={24}
        title="Print Summary"
        className="cursor-pointer"
        onClick={() => handlePrint(data)}
      />
    </div>
    <div className="w-full">
      <div
        className="overflow-y-auto pt-2 pr-2 my-2"
        style={{
          // Roughly the height that keeps the save button in view on initial page load.
          minHeight: "calc(100lvh - 40rem)",
          // Roughly the height that keeps the save button and tabs in view when scrolled past the map.
          maxHeight: "calc(100lvh - 20rem)",
        }}
      >
        <div className="bg-white rounded-lg flex flex-col gap-1 pb-2">
          <div className="px-3">
            {data?.flowmeter_section?.length ? (
              <div className="py-4 border-b border-b-gray-1">
                {data?.flowmeter_section.map((meter) => (
                  <span className="flex gap-x-4">
                    <span>Flowmeter: {meter.FM_ID}</span>
                    <span>Reading: {meter.Reading}</span>
                    {meter.Date ? (
                      <span>{moment(meter.Date).utc().format("MM/DD/YYYY")}</span>
                    ) : null}
                    <span>Note: {meter.Note}</span>
                  </span>
                ))}
              </div>
            ) : null}
            {(data?.crop_section || []).map((crop) => (
              <div className="py-4 border-b border-b-gray-1">
                <div className="text-lg">Crop Planted: {crop.Crop_Planted}</div>
                <div className="px-3 mt-3">
                  <span className="flex items-center flex-wrap gap-x-4 gap-y-2">
                    {crop.Crop_Yield ? (
                      <span>Crop Yield: {crop.Crop_Yield}</span>
                    ) : null}
                    {crop.Yield_Goal ? (
                      <span>Corn Yield Goal: {crop.Yield_Goal}</span>
                    ) : null}
                    {crop.Sprinkler_Acres ? (
                      <span>Sprinkler Acres: {crop.Sprinkler_Acres}</span>
                    ) : null}
                    {crop.Flood_Acres ? (
                      <span>Flood Acres: {crop.Flood_Acres}</span>
                    ) : null}
                    {crop.Other_Irr_Acres ? (
                      <span>Other Acres: {crop.Other_Irr_Acres}</span>
                    ) : null}
                    {crop.Irrigation_Water_Applied ? (
                      <span>
                        Irrigation Water Applied : {crop.Irrigation_Water_Applied}
                      </span>
                    ) : null}
                    {crop.Water_Nitrate ? (
                      <span>Water Nitrate: {crop.Water_Nitrate}</span>
                    ) : null}
                    {crop.Tillage_Practice ? (
                      <span>Tillage Practice: {crop.Tillage_Practice}</span>
                    ) : null}
                  </span>
                  {crop?.Nitrogen_Applications?.length ? (
                    <div className="mt-3">
                      <div className="underline">Nitrogen Applications</div>
                      {crop.Nitrogen_Applications.map((nitrogen, index) => (
                        <span className="flex flex-wrap gap-x-4 gap-y-2 mt-2">
                          <span>
                            Nitrogen Application {index + 1} -{" "}
                            {moment(nitrogen.date_applied)
                              .utc()
                              .format("MM/DD/YYYY")}
                          </span>
                          <span>Emergent Type: {nitrogen.emergent_type}</span>
                          <span>
                            Nitrogen Applied: {nitrogen.direct_nitrogen} lbs/ac
                          </span>
                          <span>Inhibitor Type: {nitrogen.inhibitor_type}</span>
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
            {data.soil_section?.length ? (
              data.soil_section.map((sample, sampleIndex) => (
                <div className="mt-4" key={sampleIndex}>
                  <div className="text-lg">
                    Soil Sample: {sample.sample_id}
                  </div>
                  <div className="px-3 mt-3">
                    <span className="flex items-center flex-wrap gap-x-4 gap-y-2">
                      <span>
                        Date Sample Taken:{" "}
                        {moment(sample.sample_date).utc().format("MM/DD/YYYY")}
                      </span>
                      <span>Lab Name: {sample.lab_name}</span>
                      <span>
                        Date on Lab Report:{" "}
                        {moment(sample.lab_date).utc().format("MM/DD/YYYY")}
                      </span>
                      <span>Acres Represented: {sample.acres_represented}</span>
                      <span>Soil Texture: {sample.soil_texture}</span>
                    </span>
                    {sample.soil_layers?.map((layer, index) => (
                      <div className="mt-3" key={index}>
                        <div className="underline">
                          Layer {index + 1}: {layer.top_depth} to {layer.bottom_depth} inches
                        </div>
                        <span className="px-3 mt-3 flex flex-wrap gap-x-4 gap-y-2">
                          <span>Organic Matter: {layer.organic_matter}%</span>
                          <span>Nitrogen: {layer.nitrate} ppm</span>
                          <span>Phosporus: {layer.phosphate} ppm</span>
                          <span>K (Potassium): {layer.potassium} ppm</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : null}
          </div>
        </div>
      </div>
    </div>

    <div className="w-full flex justify-end gap-4">
      {advanceLabel && (
        <Button
          onClick={() => advanceTab(false)}
          disabled={!advanceTab || saveStatus === 'saving'}
        >
          {advanceLabel}
        </Button>
      )}
      <Button
        className="gap-4"
        onClick={handleSubmit}
        title={readOnly ? "Report has already been accepted" : readyToSubmit ? "Submit for review" : "One or more sections have issues that must be addressed prior to submission"}
        disabled={!readyToSubmit && !readOnly}
      >
        {saveStatus === "saving" ? <><Spinner size={6} /> Submitting</> : 'Submit'}
      </Button>

    </div>
  </>);
}
